import { useStorageState } from '../useStorageState';
import { useCallback, useMemo } from 'react';
import { useVessel } from '../../contexts/VesselContext';
import { usePermissions } from '../usePermissions';

/**
 * Normalizes training records from old format to new format
 * Old: { trainee: "John Smith" }
 * New: { trainees: [{ crewId, name, position }] }
 */
const normalizeTrainingRecord = (record) => {
  // Already new format
  if (record.trainees && Array.isArray(record.trainees)) {
    return record;
  }

  // Migrate old single-trainee format
  if (record.trainee) {
    return {
      ...record,
      trainees: [{ name: record.trainee }], // No crewId for legacy records
      // Keep trainee field for backward compatibility during transition
    };
  }

  // Fallback - return as-is
  return record;
};

/**
 * Hook for managing training records
 * Follows Anchor Mode v2 - vessel scoping handled by useStorageState
 */
export function useTrainingData() {
  const { value: rawRecords = [], setValue: setRecords } = useStorageState('onboard_training', []);
  const { getCurrentVessel } = useVessel();
  const { currentUser } = usePermissions();

  // Get current user and vessel info for audit trail
  const getUserName = useCallback(() => {
    return currentUser?.name || 'Unknown User';
  }, [currentUser]);

  const getVesselName = useCallback(() => {
    const vessel = getCurrentVessel();
    return vessel?.name || vessel?.vesselName || 'Unknown Vessel';
  }, [getCurrentVessel]);

  /**
   * Format values for human-readable display in audit log
   */
  const formatValueForDisplay = (field, value) => {
    if (value === null || value === undefined) return 'Not set';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) {
      // Handle trainees array specially
      if (field === 'trainees') {
        return value.map(t => t.name || 'Unknown').join(', ');
      }
      return value.join(', ');
    }
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  /**
   * Calculate what fields changed between old and new record
   */
  const calculateChanges = useCallback((oldRecord, newRecord, fieldsToTrack) => {
    const changes = [];

    fieldsToTrack.forEach(field => {
      const oldValue = oldRecord[field];
      const newValue = newRecord[field];

      // Only track if value actually changed
      if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        changes.push({
          field: field,
          oldValue: oldValue,
          newValue: newValue,
          displayOld: formatValueForDisplay(field, oldValue),
          displayNew: formatValueForDisplay(field, newValue)
        });
      }
    });

    return changes;
  }, []);

  /**
   * Generate simple summary from changes array
   */
  const generateSummary = (changes) => {
    if (changes.length === 0) return 'Text updated';
    if (changes.length === 1) {
      const change = changes[0];
      // Make field name more readable
      const fieldName = change.field
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
      return `${fieldName} updated`;
    }
    return `${changes.length} fields updated`;
  };

  /**
   * Add audit entry to record when updated
   */
  const addAuditEntry = useCallback((existingRecord, updates, userName, vesselName, changes, reason) => {
    const now = new Date().toISOString();

    const auditEntry = {
      id: `audit_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      timestamp: now,
      user: userName,
      vessel: vesselName,
      action: 'updated',
      summary: generateSummary(changes),
      changes: changes,
      reason: reason || 'Record updated',
      ipAddress: 'N/A'
    };

    return {
      ...existingRecord,
      ...updates,
      updatedBy: userName,
      updatedAt: now,
      auditLog: [...(existingRecord.auditLog || []), auditEntry]
    };
  }, []);

  // Normalize all records on read (handles migration)
  const trainingRecords = useMemo(() =>
    rawRecords.map(normalizeTrainingRecord),
    [rawRecords]
  );

  // Add new training record
  const addTrainingRecord = useCallback((record) => {
    const newRecord = {
      ...record,
      id: record.id || `TRAIN${Date.now()}`,
      createdBy: getUserName(),  // v4.2 Day 13
      createdAt: new Date().toISOString()
    };
    setRecords(prev => [...prev, newRecord]);
    return newRecord;
  }, [setRecords, getUserName]);

  // Update existing training record with audit trail
  const updateTrainingRecord = useCallback((id, updates, userName, vesselName, reason) => {
    // Use provided names or get from context
    const user = userName || getUserName();
    const vessel = vesselName || getVesselName();

    setRecords(prev => prev.map(r => {
      if (r.id !== id) return r;

      // Fields to track for audit (important training record fields)
      const fieldsToTrack = [
        'topic', 'category', 'date', 'duration', 'trainer',
        'competencyLevel', 'description', 'trainees',
        'certificateIssued', 'certificateDetails', 'notes',
        'materialsUsed', 'assessmentMethod'
      ];

      // Calculate what changed
      const changes = calculateChanges(r, updates, fieldsToTrack);

      // Only add audit entry if something actually changed
      if (changes.length === 0) {
        return { ...r, ...updates, updatedAt: new Date().toISOString() };
      }

      // Add audit entry with changes
      return addAuditEntry(r, updates, user, vessel, changes, reason);
    }));
  }, [setRecords, calculateChanges, addAuditEntry, getUserName, getVesselName]);

  // Delete training record
  const deleteTrainingRecord = useCallback((id) => {
    setRecords(prev => prev.filter(r => r.id !== id));
  }, [setRecords]);

  // Get training record by ID
  const getTrainingById = useCallback((id) => {
    return trainingRecords.find(r => r.id === id);
  }, [trainingRecords]);

  // Get training records for specific crew member
  const getTrainingByCrewId = useCallback((crewId) => {
    return trainingRecords.filter(r =>
      r.trainees?.some(t => t.crewId === crewId)
    );
  }, [trainingRecords]);

  // Stats
  const stats = useMemo(() => {
    const now = new Date();
    const thisMonth = trainingRecords.filter(r => {
      const d = new Date(r.date);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    });

    const lastMonth = trainingRecords.filter(r => {
      const d = new Date(r.date);
      const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return d.getMonth() === lastMonthDate.getMonth() && d.getFullYear() === lastMonthDate.getFullYear();
    });

    // Group by category
    const byCategory = trainingRecords.reduce((acc, r) => {
      const cat = r.category || 'uncategorized';
      acc[cat] = (acc[cat] || 0) + 1;
      return acc;
    }, {});

    // Get unique topics
    const topics = [...new Set(trainingRecords.map(r => r.topic).filter(Boolean))];

    return {
      total: trainingRecords.length,
      thisMonth: thisMonth.length,
      lastMonth: lastMonth.length,
      byCategory,
      topics
    };
  }, [trainingRecords]);

  return {
    // Data
    trainingRecords,

    // CRUD
    addTrainingRecord,
    updateTrainingRecord,
    deleteTrainingRecord,

    // Queries
    getTrainingById,
    getTrainingByCrewId,

    // Stats
    stats
  };
}